import {
  Assessment,
  CloudDownload,
  Delete,
  Description,
  Dvr,
  Edit,
  FileCopy,
  LinkOutlined,
  Visibility,
} from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { amber, green, grey, red } from '@mui/material/colors';
import ColorIndicator from 'further-ui/components/ColorIndicator';
import { Link, useHistory } from 'react-router-dom';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import DateRangePicker from 'further-ui/components/DateRangePicker';
import { Column, Row } from 'further-ui/components/Layout';
import Menu from 'further-ui/components/Menu';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import SearchField from 'further-ui/components/SearchField';
import { useDisclosure } from 'further-ui/hooks';
import {
  CalendarDay,
  numberToCurrencyString,
  pluralize,
} from 'further-ui/utils';
import { RolePermission } from 'further-types/admin-user-role';
import { DataRoomStatus } from 'further-types/data-room';

import {
  createTrancheEditRoute,
  TRANCHE_ADD,
  TRANCHE_BESPOKE_LINK,
} from 'adminConstants/routes';
import {
  FundStatus,
  fundStatusMap,
  fundStatusToLabel,
} from 'adminConstants/fundProperties';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { FirmSelector } from 'components/FirmSelector';
import { createTrancheOnboardingLink } from 'helpers/trancheLink/trancheLink';
import { useFund } from 'hooks/data/fund/useFund';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useNotification } from 'hooks/ui/useNotification';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { match, P } from 'ts-pattern';
import { Tranche, TrancheListProps } from './types';

const PaddedContainer = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.sm),
}));

const fundStatus = Object.entries(fundStatusToLabel).map(([value, name]) => ({
  name,
  value,
}));

type TrancheRow = Tranche & { id: string };

const TrancheListRedesign = ({
  filters,
  handleChangeFilters,
  tags,
  isExportLoading,
  handleExportFunds,
  handlePerformanceFeeSimulation,
  pagination,
  tranches,
}: TrancheListProps) => {
  const [createPermission, deletePermission, updatePermission] =
    useHasPermissions([
      RolePermission.CreateTranche,
      RolePermission.DeleteTranche,
      RolePermission.EditTranche,
    ]);
  const { isSuperAdmin } = useGetRole();
  const history = useHistory();
  const deleteDisclosure = useDisclosure<TrancheRow>();
  const { remove, duplicate, exportFund } = useFund();
  const notification = useNotification();

  const useV2TrancheEdit = true;
  const trancheHref = (row: TrancheRow) =>
    createTrancheEditRoute(row._id, useV2TrancheEdit);

  const columns: Array<TableColumn<TrancheRow>> = [
    {
      label: 'Firm name',
      key: 'firmId',
      width: 200,
      render: (row) => row.firmId?.firmName,
    },
    {
      label: 'Tranche',
      key: 'fundName',
      width: 240,
      render: (row) => (
        <Column>
          <Typography variant="inherit">{row.fundName}</Typography>
          <Typography variant="body2">
            <Typography variant="h6" component="span" color="grey.600">
              {row.tag?.name}
            </Typography>{' '}
            {match(row)
              .with(
                { fundStatus: FundStatus.OPEN, startDate: P.string },
                ({ startDate }) =>
                  `Opened ${new CalendarDay(new Date(startDate)).toReadable()}`,
              )
              .with({ fundStatus: FundStatus.OPEN }, () => 'Open')
              .with(
                { fundStatus: FundStatus.CLOSED, closedDate: P.string },
                ({ closedDate }) =>
                  `Closed ${new CalendarDay(
                    new Date(closedDate),
                  ).toReadable()}`,
              )
              .with({ fundStatus: FundStatus.CLOSED }, () => 'Closed')
              .otherwise(() => 'Draft')}
          </Typography>
        </Column>
      ),
    },
    {
      label: 'Status',
      key: 'fundStatus',
      width: 100,
      render: (row) => {
        const { label, color } = match(row.fundStatus)
          .with(FundStatus.OPEN, () => ({ label: 'Open', color: green[500] }))
          .with(FundStatus.CLOSED, () => ({ label: 'Closed', color: red[500] }))
          .otherwise(() => ({ label: 'Draft', color: amber[500] }));
        return (
          <Tooltip title={label}>
            <PaddedContainer>
              <ColorIndicator color={color} rounded size={8} />
            </PaddedContainer>
          </Tooltip>
        );
      },
    },
    {
      label: 'Subscription value',
      key: 'totalInvestment',
      width: 200,
      disableSorting: true,
      render: (row) => (
        <Column>
          <Typography
            variant="inherit"
            color={row.totalInvestment ? 'inherit' : 'grey.600'}
          >
            {numberToCurrencyString(row.totalInvestment, {
              fallback: '--',
              showFallbackForZero: true,
            })}
          </Typography>
          <Typography variant="body2">
            {row.totalInvestment
              ? `from ${row.investorCount} ${pluralize(
                  'investor',
                  row.investorCount,
                )}`
              : '--'}
          </Typography>
        </Column>
      ),
    },
    {
      label: 'Data room',
      width: 130,
      disableSorting: true,
      key: 'dataRoom',
      render: (row) => {
        const isClosed = row.fundStatus === FundStatus.CLOSED && !!row.dataRoom;
        const { color, label } = match({ isClosed, dataRoom: row.dataRoom })
          .with({ isClosed: true }, () => ({
            color: grey[600],
            label:
              'This tranche had a data room, but it is no longer live because the tranche is closed.',
          }))
          .with({ dataRoom: P.nullish }, () => ({
            color: red[500],
            label: 'No data room has been created for this tranche.',
          }))
          .when(
            ({ dataRoom }) => dataRoom?.status === DataRoomStatus.Inactive,
            () => ({
              color: amber[500],
              label: 'This tranche has a data room draft.',
            }),
          )
          .otherwise(() => ({
            color: green[500],
            label: 'This tranche has a live data room.',
          }));
        return (
          <Tooltip title={label}>
            <PaddedContainer>
              <ColorIndicator color={color} rounded size={8} />
            </PaddedContainer>
          </Tooltip>
        );
      },
    },
    {
      label: 'Current value',
      key: 'investmentBalances',
      disableSorting: true,
      width: 180,
      render: (row) => (
        <Typography
          variant="inherit"
          color={
            row.investmentBalances?.investmentValue ? 'inherit' : 'grey.600'
          }
        >
          {numberToCurrencyString(row.investmentBalances?.investmentValue, {
            fallback: '--',
            showFallbackForZero: true,
          })}
        </Typography>
      ),
    },
    {
      label: 'Realised value',
      key: 'investmentBalances',
      disableSorting: true,
      width: 180,
      render: (row) => (
        <Typography
          variant="inherit"
          color={row.investmentBalances?.realisedValue ? 'inherit' : 'grey.600'}
        >
          {numberToCurrencyString(row.investmentBalances?.realisedValue, {
            fallback: '--',
            showFallbackForZero: true,
          })}
        </Typography>
      ),
    },
    {
      label: 'Invested capital',
      key: 'investmentBalances',
      disableSorting: true,
      width: 180,
      render: (row) => (
        <Column>
          <Typography
            variant="inherit"
            color={
              row.investmentBalances?.investedCapital ? 'inherit' : 'grey.600'
            }
          >
            {numberToCurrencyString(row.investmentBalances?.investedCapital, {
              fallback: '--',
              showFallbackForZero: true,
            })}
          </Typography>
          <Typography variant="body2">
            {row.investmentBalances?.investedCapital
              ? `in ${row.companyCount} ${pluralize(
                  'company',
                  row.companyCount,
                  'companies',
                )}`
              : '--'}
          </Typography>
        </Column>
      ),
    },
    {
      label: 'Uninvested capital',
      key: 'investmentBalances',
      width: 200,
      disableSorting: true,
      render: (row) => (
        <Typography
          variant="inherit"
          color={
            row.investmentBalances?.uninvestedCapital ? 'inherit' : 'grey.600'
          }
        >
          {numberToCurrencyString(row.investmentBalances?.uninvestedCapital, {
            fallback: '--',
            showFallbackForZero: true,
          })}
        </Typography>
      ),
    },
  ];

  const handleConfirmDelete = () => {
    const { _id } = deleteDisclosure.actionProps;
    remove.mutate(_id, {
      onSuccess: () => {
        if (tranches?.data?.length === 1) {
          pagination.pageBack();
        }
        deleteDisclosure.onClose();
      },
    });
  };

  const rows: Array<TrancheRow> =
    tranches.data?.map((t) => ({ ...t, id: t._id })) ?? [];

  return (
    <Page
      heading="Tranches"
      headingIcon={Description}
      withHeadingDivider
      withStickyHeader
      customActions={
        <Row spacing="sm">
          <Menu
            label="Export"
            buttonVariant="outlined"
            loading={isExportLoading}
            items={[
              {
                label: 'Tranche data',
                icon: CloudDownload,
                onClick: handleExportFunds,
                disabled: isExportLoading,
              },
              {
                label: 'Performance fee simulation',
                icon: Assessment,
                onClick: handlePerformanceFeeSimulation,
                disabled: isExportLoading,
              },
            ]}
          />
          {createPermission && (
            <Button
              size="large"
              variant="contained"
              component={Link}
              to={TRANCHE_ADD}
            >
              Add tranche
            </Button>
          )}
        </Row>
      }
      spacing="lg"
    >
      <Column spacing="lg">
        <PageFilters>
          {isSuperAdmin && (
            <Filter size="md">
              <FirmSelector
                fullWidth
                firmId={filters.firmId}
                placeholder="Select firm"
                onChange={(firmId) => handleChangeFilters({ firmId })}
              />
            </Filter>
          )}
          <Filter size="md">
            <SearchField
              fullWidth
              placeholder="Search by tranche name"
              value={filters.fundName}
              onSearch={(value) => handleChangeFilters({ fundName: value })}
              autoSearchOnDebounce
              allowEmptyQuery
            />
          </Filter>
          <Filter size="md">
            <Autocomplete
              fullWidth
              id="tagId"
              options={tags?.data ?? []}
              getOptionLabel={(option) => option?.name}
              getOptionKey={(option) => option._id}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              filterSelectedOptions
              value={filters.tagId}
              onChange={(_, newValue) =>
                handleChangeFilters({ tagId: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Fund type"
                />
              )}
            />
          </Filter>
          <Filter size="md">
            <Autocomplete
              fullWidth
              id="status"
              options={fundStatus.slice().reverse()}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.value}
              value={filters.fundStatus}
              filterSelectedOptions
              onChange={(_, newValue) =>
                handleChangeFilters({ fundStatus: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  placeholder="Select tranche status"
                />
              )}
            />
          </Filter>
          <Filter size="md">
            <DateRangePicker
              dateRange={filters.dateRange}
              onDateRangeChange={(range) =>
                handleChangeFilters({ dateRange: range })
              }
              placeholder="Search by close date"
            />
          </Filter>
        </PageFilters>
        <Table<TrancheRow, string>
          scrollable
          columns={columns}
          rows={rows}
          isLoading={tranches.isFetching}
          rowsTotalCount={tranches.total ?? 0}
          onRowClickHref={trancheHref}
          emptyMessage="No tranches found."
          pagination={{
            page: pagination.page === 0 ? 1 : pagination.page,
            rowsPerPage: pagination.rowsPerPage,
            orderBy: pagination.orderBy,
            order: pagination.order,
            handleRequestSort: pagination.handleRequestSort,
            handleChangePage: pagination.handleChangePage,
            handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
          }}
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              link: trancheHref,
              hidden: !updatePermission,
            },
            {
              label: 'View',
              icon: Visibility,
              link: trancheHref,
              hidden: updatePermission,
            },
            {
              label: 'Duplicate',
              icon: FileCopy,
              onClick: (row) => duplicate.mutate(row._id),
              hidden: !createPermission,
            },
            {
              label: 'Download subscription data',
              icon: CloudDownload,
              onClick: (row) =>
                exportFund(row._id, row.fundName, row.firmId?.firmName ?? ''),
            },
            {
              label: 'Make custom subscription link',
              icon: LinkOutlined,
              hidden: (row) =>
                !row.firmId?.domain || row.fundStatus !== fundStatusMap.open,
              onClick: (row) =>
                history.push({
                  pathname: TRANCHE_BESPOKE_LINK,
                  state: { trancheId: row._id },
                }),
            },
            {
              label: 'Copy tranche URL to clipboard',
              icon: Dvr,
              hidden: (row) =>
                !row.firmId?.domain || row.fundStatus !== fundStatusMap.open,
              onClick: (row) => {
                navigator.clipboard.writeText(createTrancheOnboardingLink(row));
                notification.success('Tranche link copied to clipboard.');
              },
            },
            {
              label: 'Delete',
              icon: Delete,
              hidden: !deletePermission,
              onClick: (row) => deleteDisclosure.stageAction(row),
            },
          ]}
        />
        <ConfirmDialog
          open={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          title="Are you sure you want to delete this tranche?"
          content="This cannot be undone and all information relating to the tranche will be lost."
          actions={{
            confirm: {
              onClick: handleConfirmDelete,
              label: 'Delete tranche',
            },
            cancel: {
              label: 'Cancel',
            },
          }}
        />
      </Column>
    </Page>
  );
};

export default TrancheListRedesign;
